import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect, useMemo } from 'react';
import { ReportsFilters } from './ReportsFilters';
import { ReportsTable } from './ReportsTable';
import { ReportsMobileList } from './ReportsMobileList';
import { ReportsEmptyState } from './ReportsEmptyState';
import { Skeleton } from '@/components/ui/skeleton';
import {
  CaptureRecord,
  ReportsFilters as Filters,
  ReportsPagination,
  ReportsSorting,
  VideoOption,
} from './types';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useVSLeadsVideos } from '@/hooks/useVSLeadsVideos';
import { vsleadsService } from '@/services/vsleadsService';
import { buildPresetRange } from '@/utils/period-presets';
import { endOfDay, startOfDay } from 'date-fns';

interface VSLeadsReportsTabProps {
  preselectedVideoId?: string | null;
}

// Intervalo inclusivo para o backend: do começo do primeiro dia ao fim do último.
function toReportRange({ from, to }: Filters['dateRange']) {
  return { dateFrom: startOfDay(from).toISOString(), dateTo: endOfDay(to).toISOString() };
}

export const VSLeadsReportsTab: React.FC<VSLeadsReportsTabProps> = ({ preselectedVideoId }) => {
  useLanguage();
  const { toast } = useToast();
  const { user } = useAuth();

  // Vídeos com captura ativa — alimentam só o dropdown de filtro.
  const { videos: vsLeadsVideos, isLoading: videosLoading } = useVSLeadsVideos(user?.id || null);

  const videos: VideoOption[] = useMemo(
    () =>
      vsLeadsVideos.map((v) => ({
        id: v.videoId,
        title: v.videoTitle,
        folder: null,
        thumbnail: v.videoThumbnail || '',
        duration: '',
        uploadDate: new Date(v.createdAt).toLocaleDateString(getInterfaceLanguage()),
      })),
    [vsLeadsVideos],
  );

  const [records, setRecords] = useState<CaptureRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const [filters, setFilters] = useState<Filters>({
    videoId: '',
    dateRange: buildPresetRange('last30Days', new Date()),
    captureTypes: ['data_capture', 'password_capture'],
    hasEmail: null,
    hasPhone: null,
    search: '',
  });

  const [pagination, setPagination] = useState<ReportsPagination>({
    page: 1,
    pageSize: 25,
    totalRecords: 0,
  });

  const [sorting, setSorting] = useState<ReportsSorting>({
    field: 'created_at',
    direction: 'desc',
  });

  // Aplicar vídeo pré-selecionado (vindo do card de estratégia).
  useEffect(() => {
    if (preselectedVideoId) {
      setFilters((prev) => ({ ...prev, videoId: preselectedVideoId }));
    }
  }, [preselectedVideoId]);

  // Strings estáveis do período: o objeto Date muda de identidade a cada render.
  const { dateFrom, dateTo } = toReportRange(filters.dateRange);

  // Volta pra página 1 quando filtros/ordenação mudam (evita pedir uma página
  // que não existe mais no novo conjunto).
  useEffect(() => {
    setPagination((p) => (p.page === 1 ? p : { ...p, page: 1 }));
  }, [
    filters.videoId,
    filters.search,
    dateFrom,
    dateTo,
    sorting.field,
    sorting.direction,
  ]);

  // Busca a página atual no backend sempre que filtro/ordenação/página mudam.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    vsleadsService
      .getLeadsReport({
        page: pagination.page,
        pageSize: pagination.pageSize,
        videoId: filters.videoId || undefined,
        dateFrom,
        dateTo,
        search: filters.search || undefined,
        sortField: sorting.field,
        sortDir: sorting.direction,
      })
      .then((res) => {
        if (cancelled) return;
        setRecords(
          res.data.map((l) => ({
            id: l.id,
            video_id: l.id_video,
            video_name: l.video_name,
            type: l.type,
            name: l.nome || '',
            email: l.email || '',
            phone: l.telefone || '',
            created_at_utc: l.createdAt,
          })),
        );
        setPagination((p) => ({ ...p, totalRecords: res.total }));
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        setHasLoadedOnce(true);
      });
    return () => {
      cancelled = true;
    };
  }, [
    pagination.page,
    pagination.pageSize,
    filters.videoId,
    filters.search,
    dateFrom,
    dateTo,
    sorting.field,
    sorting.direction,
  ]);

  const handleExportCSV = (rows: CaptureRecord[]) => {
    const headers = [
      'video_id',
      'video_name',
      'type',
      'name',
      'email',
      'phone',
      'captured_at_utc',
      'captured_at_local',
    ];
    const lines = rows.map((record) => {
      const date = new Date(record.created_at_utc);
      return [
        record.video_id,
        record.video_name,
        record.type,
        record.name,
        record.email,
        record.phone,
        record.created_at_utc,
        date.toLocaleString(getInterfaceLanguage()),
      ]
        .map((field) => `"${field}"`)
        .join(',');
    });
    const csvContent = [headers.join(','), ...lines].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `vsleads_capturas_${Date.now()}.csv`;
    link.click();
    toast({
      title: translateInterfaceText("CSV exportado com sucesso!"),
      description: translateInterfaceText("{value0} registro(s) exportado(s).", { value0: (rows.length) }),
    });
  };

  const handleExportRow = (record: CaptureRecord) => handleExportCSV([record]);

  const handleDelete = () => {
    toast({
      title: translateInterfaceText("Funcionalidade em desenvolvimento"),
      description: translateInterfaceText("A exclusão de registros estará disponível em breve."),
    });
  };

  // Estado vazio: nenhum vídeo com captura de leads ativa.
  if (!videosLoading && vsLeadsVideos.length === 0) {
    return <ReportsEmptyState type="no-strategies" />;
  }

  // Skeleton só no primeiro carregamento; refetch de filtro/página mantém a tabela.
  const showSkeleton = !hasLoadedOnce && (loading || videosLoading);

  return (
    <div className="space-y-6">
      {showSkeleton ? (
        <>
          <Skeleton className="h-12" />
          <Skeleton className="h-10" />
          <Skeleton className="h-96" />
        </>
      ) : (
        <>
          <div className="space-y-6">
            <ReportsFilters
              filters={filters}
              onFiltersChange={setFilters}
              recordCount={pagination.totalRecords}
              videos={videos}
              selectedVideoId={filters.videoId}
              onSelectVideo={(videoId) => setFilters({ ...filters, videoId })}
              isLoadingVideos={videosLoading}
            />

            {records.length === 0 ? (
              <ReportsEmptyState
                type="no-data"
                onClearFilters={() =>
                  setFilters({
                    ...filters,
                    dateRange: buildPresetRange('last30Days', new Date()),
                    captureTypes: ['data_capture', 'password_capture'],
                    hasEmail: null,
                    hasPhone: null,
                    search: '',
                  })
                }
              />
            ) : (
              <>
                <div className="hidden lg:block">
                  <ReportsTable
                    records={records}
                    pagination={pagination}
                    sorting={sorting}
                    onPaginationChange={setPagination}
                    onSortingChange={setSorting}
                    onExportRow={handleExportRow}
                    onDelete={handleDelete}
                  />
                </div>
                <div className="lg:hidden">
                  <ReportsMobileList
                    records={records}
                    pagination={pagination}
                    onPaginationChange={setPagination}
                    onExportRow={handleExportRow}
                    onDelete={handleDelete}
                  />
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};
